<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import { STATUS_OPTIONS, STATUS_CONFIG, SIZE_CLASSES } from './constants';

const props = defineProps({
  status: {
    type: String,
    required: true,
    validator: value => STATUS_OPTIONS.includes(value),
  },
  /**
   * Required when `status` is `unavailable`: an unavailable state must say why
   * (no verified endpoint, plan, feature flag, provider or permission).
   */
  reason: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    default: 'md',
    validator: value => Object.keys(SIZE_CLASSES).includes(value),
  },
  showIcon: {
    type: Boolean,
    default: true,
  },
  /** Renders muted, for chips describing a disabled or inactive resource. */
  disabled: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const config = computed(() => STATUS_CONFIG[props.status]);

const label = computed(() => {
  if (config.value.requiresReason) {
    if (!props.reason) {
      // A missing reason is a developer error, not a runtime condition: the
      // design contract requires every unavailable state to be explained.
      throw new Error(
        `StatusChip: status "${props.status}" requires a "reason" prop.`
      );
    }

    return t('STATUS_CHIP.UNAVAILABLE_WITH_REASON', { reason: props.reason });
  }

  return t(config.value.labelKey);
});

const isBusy = computed(() => Boolean(config.value.busy));
</script>

<template>
  <span
    v-tooltip="label"
    class="inline-flex items-center justify-center max-w-full rounded-md shrink-0"
    :class="[config.classes, SIZE_CLASSES[size], { 'opacity-50': disabled }]"
    :aria-busy="isBusy ? 'true' : undefined"
  >
    <Icon
      v-if="showIcon"
      :icon="config.icon"
      class="size-3 flex-shrink-0"
      :class="{ 'animate-spin motion-reduce:animate-none': isBusy }"
      aria-hidden="true"
    />
    <span class="truncate">{{ label }}</span>
  </span>
</template>
